import React, { useState, useEffect } from 'react';
import { Calendar, BookOpen, ArrowRight, Quote, Eye, Heart, Award, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

interface ColumnArticle {
  id: number;
  title: string;
  subtitle: string;
  content: string;
  author: {
    name: string;
    title: string;
    affiliation: string;
    bio: string;
    image: string;
  };
  date: string;
  category: string;
  image: string;
  views: number;
  likes: number;
  tags: string[];
  featured: boolean;
}

const ColumnPage: React.FC = () => {
  const [columns, setColumns] = useState<ColumnArticle[]>([]);
  const [selectedColumn, setSelectedColumn] = useState<ColumnArticle | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [likedColumns, setLikedColumns] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem('likedColumns');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  const categories = ['전체', 'AI 리터러시', 'AI 윤리', '기술 혁신', '교육', '사회적 영향', '미래 전망'];

  useEffect(() => {
    fetchColumns();
  }, []);

  const fetchColumns = async () => {
    try {
      const response = await fetch('/api/columns');
      const data = await response.json();
      const sorted = [...data].sort((a: ColumnArticle, b: ColumnArticle) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setColumns(sorted);
    } catch (error) {
      console.error('컬럼 데이터 로드 실패:', error);
      setColumns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (e: React.MouseEvent, columnId: number) => {
    e.stopPropagation();
    if (likedColumns.has(columnId)) return;
    try {
      const res = await fetch(`/api/columns/${columnId}/like`, { method: 'POST' });
      const data = await res.json();
      setColumns(prev => prev.map(col => col.id === columnId ? { ...col, likes: data.likes } : col));
      if (selectedColumn?.id === columnId) {
        setSelectedColumn(prev => prev ? { ...prev, likes: data.likes } : prev);
      }
      const newLiked = new Set(likedColumns).add(columnId);
      setLikedColumns(newLiked);
      localStorage.setItem('likedColumns', JSON.stringify(Array.from(newLiked)));
    } catch (err) {
      console.error('좋아요 실패:', err);
    }
  };

  const openColumn = async (column: ColumnArticle) => {
    setSelectedColumn(column);
    setShowModal(true);
    try {
      const res = await fetch(`/api/columns/${column.id}`);
      const data = await res.json();
      setSelectedColumn(data);
      setColumns(prev => prev.map(col => col.id === column.id ? { ...col, views: data.views } : col));
    } catch (err) {
      // API 실패 시 기존 데이터 유지
    }
  };

  const filteredColumns = selectedCategory === '전체'
    ? columns
    : columns.filter(col => col.category === selectedCategory);

  const featuredColumns = columns.filter(col => col.featured);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-teal-50/30 to-teal-50">
      <Header />
      <div className="h-16" />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-navy-950 via-navy-900 to-purple-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0di00aC0ydjRoLTR2Mmg0djRoMnYtNGg0di0yaC00em0wLTMwVjBoLTJ2NGgtNHYyaDR2NGgyVjZoNFY0aC00ek02IDM0di00SDR2NEgwdjJoNHY0aDJ2LTRoNHYtMkg2ek02IDRWMEG0djRIMHYyaDR2NGgyVjZoNFY0SDZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex items-center justify-center mb-6">
            <Quote className="h-12 w-12 text-teal-300 mr-4" />
            <h1 className="text-5xl md:text-6xl font-bold">
              인간-AI 협력 컬럼
            </h1>
          </div>

          <p className="text-xl text-teal-200 text-center max-w-3xl mx-auto leading-relaxed">
            AI와 인간의 협력에 대한 깊이 있는 통찰과 전문가들의 혜안을 만나보세요
          </p>
        </div>
      </section>

      {/* Featured Section */}
      {!loading && featuredColumns.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center mb-12">
              <Award className="h-8 w-8 text-amber-500 mr-3" />
              <h2 className="text-3xl font-bold text-gray-900">주목할 만한 글</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {featuredColumns.map((column) => (
                <article
                  key={column.id}
                  className="group relative bg-gradient-to-br from-white to-teal-50 rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 border border-teal-100"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-400 to-orange-500 opacity-10 rounded-bl-full"></div>

                  <div className="p-8 relative">
                    <div className="flex items-center space-x-3 mb-4">
                      <span className="bg-gradient-to-r from-amber-400 to-orange-500 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-md">
                        Featured
                      </span>
                      <span className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-sm font-medium">
                        {column.category}
                      </span>
                    </div>

                    <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                      {column.title}
                    </h3>

                    <p className="text-lg text-teal-600 font-medium mb-4 italic">
                      {column.subtitle}
                    </p>

                    <div className="flex items-center space-x-4 mb-6">
                      <div className="flex items-center">
                        {column.author.image ? (
                          <img
                            src={column.author.image}
                            alt={column.author.name}
                            className="w-12 h-12 rounded-full object-cover mr-3 border-2 border-teal-200"
                            onError={(e) => {
                              const t = e.target as HTMLImageElement;
                              t.style.display = 'none';
                              t.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-navy-600 flex items-center justify-center text-white font-bold text-lg mr-3 ${column.author.image ? 'hidden' : ''}`}>
                          {column.author.name[0]}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{column.author.name}</div>
                          <div className="text-sm text-gray-600">{column.author.affiliation}</div>
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-700 mb-6 line-clamp-3 leading-relaxed">
                      {column.content}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {formatDate(column.date)}
                        </div>
                        <div className="flex items-center">
                          <Eye className="h-4 w-4 mr-1" />
                          {column.views}
                        </div>
                        <button
                          onClick={(e) => handleLike(e, column.id)}
                          className={`flex items-center gap-1 transition-colors ${likedColumns.has(column.id) ? 'text-rose-500' : 'text-gray-400 hover:text-rose-500'}`}
                        >
                          <Heart className={`h-4 w-4 ${likedColumns.has(column.id) ? 'fill-current' : ''}`} />
                          {column.likes}
                        </button>
                      </div>

                      <button
                        onClick={() => openColumn(column)}
                        className="inline-flex items-center text-teal-600 hover:text-teal-800 font-semibold group-hover:translate-x-1 transition-transform"
                      >
                        전문 읽기
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      {!loading && columns.length > 0 && (
        <section className="py-8 bg-white border-y">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-teal-500 to-navy-600 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Columns Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
              <p className="text-gray-600">컬럼을 불러오는 중...</p>
            </div>
          ) : filteredColumns.length === 0 ? (
            <div className="text-center py-20">
              <div className="max-w-2xl mx-auto">
                <div className="mb-8">
                  <BookOpen className="h-24 w-24 text-teal-300 mx-auto mb-6" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  곧 새로운 컬럼이 시작됩니다
                </h3>
                <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                  <p>
                    인간-AI 협력 분야의 저명한 전문가들을 섭외 중입니다.
                  </p>
                  <p>
                    AI 윤리, 기술 혁신, 교육, 사회적 영향 등 다양한 주제로
                    <br />
                    깊이 있는 통찰과 혜안을 나눌 예정입니다.
                  </p>
                  <div className="mt-8 pt-8 border-t border-gray-200">
                    <p className="text-xl font-semibold text-teal-600">
                      많은 기대 부탁드립니다!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredColumns.filter(col => !col.featured).map((column) => (
                <article
                  key={column.id}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                >
                  <div className="h-2 bg-gradient-to-r from-teal-500 to-navy-600"></div>

                  <div className="p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <span className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-xs font-semibold">
                        {column.category}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(column.date)}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                      {column.title}
                    </h3>

                    <p className="text-sm text-teal-600 font-medium mb-4 italic line-clamp-1">
                      {column.subtitle}
                    </p>

                    <div className="flex items-center space-x-3 mb-4 pb-4 border-b">
                      {column.author.image ? (
                        <img
                          src={column.author.image}
                          alt={column.author.name}
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-teal-200"
                          onError={(e) => {
                            const t = e.target as HTMLImageElement;
                            t.style.display = 'none';
                            t.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-navy-600 flex items-center justify-center text-white font-bold flex-shrink-0 ${column.author.image ? 'hidden' : ''}`}>
                        {column.author.name[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 text-sm truncate">{column.author.name}</div>
                        <div className="text-xs text-gray-600 truncate">{column.author.affiliation}</div>
                      </div>
                    </div>

                    <p className="text-gray-700 text-sm mb-4 line-clamp-3 leading-relaxed">
                      {column.content}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 text-xs text-gray-500">
                        <div className="flex items-center">
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          {column.views}
                        </div>
                        <button
                          onClick={(e) => handleLike(e, column.id)}
                          className={`flex items-center gap-1 transition-colors ${likedColumns.has(column.id) ? 'text-rose-500' : 'text-gray-400 hover:text-rose-500'}`}
                        >
                          <Heart className={`h-3.5 w-3.5 ${likedColumns.has(column.id) ? 'fill-current' : ''}`} />
                          {column.likes}
                        </button>
                      </div>

                      <button
                        onClick={() => openColumn(column)}
                        className="inline-flex items-center text-teal-600 hover:text-teal-800 font-medium text-sm group-hover:translate-x-1 transition-transform"
                      >
                        읽기
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Modal */}
      {showModal && selectedColumn && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-teal-500 to-navy-600 text-white p-6 rounded-t-2xl z-10">
              <div className="flex justify-between items-start">
                <div className="flex-1 pr-4">
                  <span className="inline-block bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium mb-3">
                    {selectedColumn.category}
                  </span>
                  <h2 className="text-3xl font-bold mb-2">
                    {selectedColumn.title}
                  </h2>
                  <p className="text-lg text-teal-100 italic">
                    {selectedColumn.subtitle}
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <span className="text-3xl leading-none">×</span>
                </button>
              </div>
            </div>

            <div className="p-8">
              {/* Hero Image */}
              {selectedColumn.image && (
                <div className="rounded-xl overflow-hidden mb-8 shadow-md border border-gray-100">
                  <img
                    src={selectedColumn.image}
                    alt={selectedColumn.title}
                    className="w-full h-auto block"
                  />
                </div>
              )}

              {/* Author Info */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 mb-8 border border-teal-100">
                <div className="flex items-start space-x-5">
                  {selectedColumn.author.image ? (
                    <img
                      src={selectedColumn.author.image}
                      alt={selectedColumn.author.name}
                      className="w-20 h-20 rounded-full object-cover flex-shrink-0 border-2 border-teal-200 shadow-sm"
                      onError={(e) => {
                        const t = e.target as HTMLImageElement;
                        t.style.display = 'none';
                        t.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={`w-20 h-20 rounded-full bg-gradient-to-br from-teal-500 to-navy-600 flex items-center justify-center text-white font-bold text-2xl flex-shrink-0 ${selectedColumn.author.image ? 'hidden' : ''}`}>
                    {selectedColumn.author.name[0]}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-0.5">
                      {selectedColumn.author.name}
                    </h3>
                    <div className="text-teal-600 font-medium text-sm mb-0.5">
                      {selectedColumn.author.title} · {selectedColumn.author.affiliation}
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed mt-2">
                      {selectedColumn.author.bio.split(/(\[.*?\]\(.*?\))/).map((part, i) => {
                        const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
                        if (linkMatch) {
                          return <a key={i} href={linkMatch[2]} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-500 underline underline-offset-2">{linkMatch[1]}</a>;
                        }
                        return <span key={i}>{part}</span>;
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content — paragraph-aware rendering */}
              <div className="prose prose-lg max-w-none">
                <div className="text-gray-800 leading-[1.95] text-[1.05rem] text-justify space-y-5">
                  {selectedColumn.content.split('\n\n').map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-6 border-t">
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedColumn.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* 면책 고지 */}
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 mb-6">
                  <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 leading-relaxed">
                    본 컬럼은 저자의 고유한 저작물로, 인간-AI 협력 연구센터(CHAIR)의 공식 입장이나 견해를 대표하지 않습니다.
                  </p>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(selectedColumn.date)}
                    </div>
                    <div className="flex items-center">
                      <Eye className="h-4 w-4 mr-1" />
                      조회 {selectedColumn.views}
                    </div>
                    <button
                      onClick={(e) => handleLike(e, selectedColumn.id)}
                      className={`flex items-center gap-1 px-3 py-1 rounded-full transition-all ${likedColumns.has(selectedColumn.id) ? 'text-rose-500 bg-rose-50' : 'text-gray-400 hover:text-rose-500 hover:bg-rose-50'}`}
                    >
                      <Heart className={`h-4 w-4 ${likedColumns.has(selectedColumn.id) ? 'fill-current' : ''}`} />
                      좋아요 {selectedColumn.likes}
                    </button>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end mt-8 pt-6 border-t">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-8 py-3 bg-gradient-to-r from-teal-500 to-navy-600 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-navy-800 transition-all shadow-md hover:shadow-lg"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ColumnPage;
